<template>
    <div id="search-bar" ref="searchBar">
        <div class="input" @click="() => toggleSearch(true)" :class="{ active: resultActive }">
            <div class="material-icons search-icon">search</div>
            <KeyboardShortcut v-if="selectedSearch">{{ $t(`search.prefix.${selectedSearch}`) }}</KeyboardShortcut>
            <div @click="() => toggleSearch(true)" class="typer-wrapper" v-show="!resultActive" id="typeWriter">
                <vue-typer
                    v-if="!isTherePrefix"
                    :text="$_.shuffle(getTyperSentences)"
                    :repeat="Infinity"
                    :shuffle="false"
                    initial-action="erasing"
                    :pre-type-delay="70"
                    :type-delay="70"
                    :pre-erase-delay="2000"
                    :erase-delay="250"
                    erase-style="select-all"
                    :erase-on-complete="false"
                    caret-animation="blink"
                />
            </div>

            <input ref="searchInput" id="search-bar-input" v-model="searchText" autocomplete="off" />
            <div class="keyboard-shortcut">
                <KeyboardShortcut v-if="!isAnyPrefixTyped">{{ isMacOS ? "⌘" : "CTRL +" }} K</KeyboardShortcut>

                <KeyboardShortcut v-else>
                    {{ $t("search.tapTo", { prefix: capitalizeFirstLetter(searchText) }) }}
                </KeyboardShortcut>
            </div>
        </div>

        <div class="result-search" v-show="resultActive" :class="{ active: resultActive }">
            <div class="empty" v-if="searchText.length <= 0">
                <div class="material-icons search-icon">search</div>
                <div class="info-title">{{ $t("search.searchAreaTitle") }}</div>
                <div class="info-description">
                    {{ $t("search.searchAreaDesc1") }}
                    <KeyboardShortcut>{{ $t(`search.prefix.jump`) }}</KeyboardShortcut>
                    {{ $t("search.searchAreaDesc2") }}
                    <KeyboardShortcut>{{ $t(`search.prefix.new`) }}</KeyboardShortcut>
                    {{ $t("search.searchAreaDesc3") }}
                </div>
            </div>
            <div class="items" v-else-if="results.length > 0">
                <div
                    class="item"
                    :id="`item-${index}`"
                    v-for="(item, index) of results"
                    :class="[
                        {
                            active: index === navigationIndex,
                            'centered-name': !item.keywords || item.keywords.length === 0,
                        },
                    ]"
                    @click="action(item)"
                >
                    <KeyboardShortcut>{{ $t(`search.prefix.${item.prefix}`) }}</KeyboardShortcut>
                    <div class="content">
                        <div class="name">{{ getName(item.name) }}</div>
                        <div class="keywords">
                            <div v-for="keyword of (item.keywords || []).slice(0, 2)" style="font-weight: bold">
                                {{ keyword }}
                            </div>
                            <div v-for="keyword of (item.keywords || []).slice(2, 6)">
                                {{ keyword }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="empty" v-else>
                <div class="material-icons search-icon">sentiment_dissatisfied</div>
                <div class="info-title">Not Found Anything</div>
            </div>
            <div class="result-bottom">
                <KeyboardShortcut>
                    <div class="material-icons keyboard-icon">keyboard_arrow_up</div>
                </KeyboardShortcut>
                <KeyboardShortcut>
                    <div class="material-icons keyboard-icon">keyboard_arrow_down</div>
                </KeyboardShortcut>
                <div class="text">{{ $t("search.navigateBtn") }}</div>
                <KeyboardShortcut>
                    <div class="material-icons keyboard-icon">keyboard_return</div>
                </KeyboardShortcut>
                <div class="text">{{ $t("search.selectBtn") }}</div>
                <KeyboardShortcut>ESC</KeyboardShortcut>
                <div class="text">{{ $t("search.cancelBtn") }}</div>
            </div>
            <!-- <div class="search-item">asd</div> -->
        </div>
        <a-modal
            v-if="openRespresentativeOptions"
            class="representativeSettings"
            :destroyOnClose="true"
            :footer="null"
            :title="$t('message.represantativeSettings')"
            :visible="true"
            :width="700"
            style="top: 20px"
            @cancel="() => (openRespresentativeOptions = false)"
        >
            <RepresentativeSettings :is-target-notification="openRespresentativeOptions" />
        </a-modal>
    </div>
</template>
<script>
import KeyboardShortcut from "./KeyboardShortcut.vue";
import { searchVisitor } from "../../api/visitor";
import staticSearchElements from "./staticSearchElements.js";
import { mapState, mapActions } from "vuex";
import Fuse from "fuse.js";
import { filter } from "rxjs/operators";
import SYSTEM_PUBSUB, { systemMessageStream } from "../../utils/systemPubSub";
import RepresentativeSettings from "../RepresentativeSettings.vue";

export default {
    components: {
        RepresentativeSettings,
        KeyboardShortcut,
    },
    data: () => {
        return {
            searchText: "",
            resultActive: false,
            results: [
                {
                    type: "visitor",
                    action: "model-visitor",
                    target: "id",
                    name: "Mehmet",
                },
                {
                    type: "jump",
                    action: "navigator",
                    target: "/mail",
                    name: "Mail",
                },
                {
                    type: "new",
                    action: "navigator",
                    target: "/mail",
                    name: "Send Mail",
                },
                {
                    type: "dm",
                    action: "model-user",
                    target: "id",
                    name: "Mehmet",
                },
            ],
            isMacOS: false,
            navigationIndex: 0,
            selectedSearch: null,
            debounce: null,
            listeners: [],
            isTherePrefix: false,
            typerSentences: {
                en: [
                    "New ⇥ Users",
                    "New ⇥ Tags",
                    "New ⇥ Role",
                    "New ⇥ Triggers",
                    "New ⇥ Department",
                    "New ⇥ Ticket",
                    "Jump ⇥ Settings",
                    "Jump ⇥ Integrations",
                    "Jump ⇥ Team",
                    "Jump ⇥ Users",
                    "Jump ⇥ Report",
                    "DM ⇥ John",
                    "Timing Settings",
                    "visitor@mail.com",
                    "Language and Region",
                    "Theme",
                    "Appearance",
                ],
                tr: [
                    "Yeni ⇥ Kullanıcı",
                    "Yeni ⇥ Etiket",
                    "Yeni ⇥ Rol",
                    "Yeni ⇥ Tetikleyici",
                    "Yeni ⇥ Departman",
                    "Yeni ⇥ Etiketler",
                    "Git ⇥ Ayarlar",
                    "Git ⇥ Entegrasyonlar",
                    "Git ⇥ Takım",
                    "Git ⇥ Kullanıcılar",
                    "Git ⇥ Rapor",
                    "DM ⇥ Enes",
                    "Zamanlama ayarları",
                    "Ziyaretçi@mail.com",
                    "Dil ve Bölge",
                    "Tema",
                    "Görünüş - Görünüm",
                ],
                ru: [
                    "Новые ⇥ пользователи",
                    "Новые ⇥ метки",
                    "Новые ⇥ роли",
                    "Новые ⇥ триггеры",
                    "Новый ⇥ отдел",
                    "Новый ⇥ билет",
                    "Перейти ⇥ Настройки",
                    "Перейти ⇥ Интеграции",
                    "Перейти ⇥ Команда",
                    "Перейти ⇥ Пользователи",
                    "Перейти ⇥ Отчет",
                    "DM ⇥ John",
                    "Настройки тайминга",
                    "visitor@mail.com",
                    "Язык и регион",
                    "Тема",
                    "Внешний вид",
                ],
                de: [
                    "Neue ⇥ Benutzer",
                    "Neue ⇥ Tags",
                    "Neue ⇥ Rolle",
                    "Neu ⇥ Auslöser",
                    "Neue ⇥ Abteilung",
                    "Neues ⇥ Ticket",
                    "Springen ⇥ Einstellungen",
                    "Springen ⇥ Integrationen",
                    "Springen ⇥ Team",
                    "Springen ⇥ Benutzer",
                    "Springen ⇥ Bericht",
                    "DM ⇥ John",
                    "Timing-Einstellungen",
                    "visitor@mail.com",
                    "Sprache und Region",
                    "Thema",
                    "Erscheinungsbild",
                ],
                fr: [
                    "Nouveau ⇥ Utilisateurs",
                    "Nouveau ⇥ Tags",
                    "Nouveau ⇥ Rôle",
                    "Nouveau ⇥ Déclencheurs",
                    "Nouveau ⇥ Département",
                    "Nouveau ⇥ Ticket",
                    "Sauter ⇥ Paramètres",
                    "Sauter ⇥ Intégrations",
                    "Sauter ⇥ Équipe",
                    "Sauter ⇥ Utilisateurs",
                    "Sauter ⇥ Rapport",
                    "DM ⇥ John",
                    "Paramètres de chronométrage",
                    "visitor@mail.com",
                    "Langue et région",
                    "Thème",
                    "Apparence",
                ],
                es: [
                    "Nuevos ⇥ Usuarios",
                    "Nuevo ⇥ Etiquetas",
                    "Nuevo ⇥ Rol",
                    "Nuevo ⇥ Activadores",
                    "Nuevo ⇥ Departamento",
                    "Nuevo ⇥ Ticket",
                    "Saltar ⇥ Ajustes",
                    "Saltar ⇥ Integraciones",
                    "Saltar ⇥ Equipo",
                    "Saltar ⇥ Usuarios",
                    "Saltar ⇥ Informe",
                    "DM ⇥ Juan",
                    "Ajustes de tiempo",
                    "visitor@mail.com",
                    "Idioma y región",
                    "Tema",
                    "Aspecto",
                ],
                ar: [
                    "جديد ⇥ المستخدمون",
                    "جديد ⇥ العلامات",
                    "جديد ⇥ دور",
                    "جديد ⇥ المشغلات",
                    "جديد ⇥ قسم",
                    "جديد ⇥ تذكرة",
                    "قفزة ⇥ إعدادات",
                    "الانتقال ⇥ التكامل",
                    "الانتقال ⇥ فريق",
                    "الانتقال ⇥ المستخدمون",
                    "الانتقال ⇥ تقرير",
                    "DM ⇥ جون",
                    "إعدادات التوقيت",
                    "visitor@mail.com",
                    "اللغة والمنطقة",
                    "السمة",
                    "المظهر",
                ],
                it: [
                    "Nuovi ⇥ Utenti",
                    "Nuovi ⇥ Tag",
                    "Nuovo ⇥ Ruolo",
                    "Nuovi ⇥ Trigger",
                    "Nuovo ⇥ Dipartimento",
                    "Nuovo ⇥ Biglietto",
                    "Salta ⇥ Impostazioni",
                    "Salta ⇥ Integrazioni",
                    "Salta ⇥ Team",
                    "Salta ⇥ Utenti",
                    "Salta ⇥ Rapporto",
                    "DM ⇥ Giovanni",
                    "Impostazioni di cronometraggio",
                    "visitor@mail.com",
                    "Lingua e regione",
                    "Tema",
                    "Aspetto",
                ],
            },
            isAnyPrefixTyped: false,
            openRespresentativeOptions: false,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
            customerWebhook: (state) => state.customerWebhook.allWebHooks,
            users: (state) => state.user.users.data,
        }),
        getTyperSentences() {
            const lang = this.$i18n.locale === "en" || this.$i18n.locale === "tr" ? this.$i18n.locale : "en";
            return this.typerSentences[lang];
        },
    },
    methods: {
        ...mapActions({
            setVisitorDetailPopup: "app/setVisitorDetailPopup",
            addNewWindow: "app/addNewWindow",
        }),
        capitalizeFirstLetter(text) {
            return text.charAt(0).toUpperCase() + text.slice(1);
        },
        startDM(id) {
            this.$router.push("/chat?tabIndex=6");
            setTimeout(() => {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.START_AGENT_CHAT, { userId: id });
            }, 500);
        },

        getName(name) {
            const result = typeof name == "string" ? name : name[0]; /*Array.isArray(name) ? name[0] : name;*/
            return result;
        },
        toggleSearch(val) {
            this.resultActive = val === false || val === true ? val : !this.resultActive;
        },
        action(item) {
            item.action();
            this.clear();
            this.resultActive = false;
        },
        clear() {
            this.navigationIndex = 0;
            this.selectedSearch = null;
            this.searchText = "";
        },
        onKeyboardShortcut() {
            this.isMacOS = !navigator.userAgent.includes("Windows");

            const clickListener = (e) => {
                const searchBar = this.$refs.searchBar;
                const typeWriterEl = document.getElementById("typeWriter");
                if (!searchBar?.contains(e.target) && !typeWriterEl?.contains(e.target)) this.toggleSearch(false);
            };
            document.addEventListener("click", clickListener);

            const documentKeyDownFn = (event) => {
                if (event.key === "Escape") {
                    if (!this.selectedSearch) this.toggleSearch(false);
                    this.clear();
                } else if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    const searchInput = document.getElementById("search-bar-input");
                    if (this.resultActive === false) searchInput?.focus();
                    this.toggleSearch();
                    this.clear();
                }
            };
            const inputKeydownFn = (event) => {
                if (event.key === "ArrowUp") {
                    event.preventDefault();
                    this.navigationIndex =
                        this.navigationIndex === 0 ? this.results.length - 1 : this.navigationIndex - 1;
                    const item = document.getElementById(`item-${this.navigationIndex}`);
                    item?.scrollIntoViewIfNeeded({ block: "start", behavior: "smooth", inline: "nearest" });
                    event.stopPropagation();
                } else if (event.key === "ArrowDown") {
                    event.preventDefault();
                    this.navigationIndex =
                        this.navigationIndex === this.results.length - 1 ? 0 : this.navigationIndex + 1;
                    const item = document.getElementById(`item-${this.navigationIndex}`);
                    item?.scrollIntoView({ block: "end", behavior: "smooth", inline: "nearest" });
                    event.stopPropagation();
                } else if (event.key === "Enter") {
                    const item = document.getElementById(`item-${this.navigationIndex}`);
                    item?.click();
                } else if (event.key === "Tab") {
                    event.preventDefault();
                    let prefix = this.searchText.toLowerCase();
                    let prefixies = {
                        new: this.$t("search.prefix.new").toLowerCase(),
                        jump: this.$t("search.prefix.jump").toLowerCase(),
                        visitor: this.$t("search.prefix.visitor").toLowerCase(),
                        dm: "dm".toLowerCase(),
                    };
                    if (
                        [prefixies.new, prefixies.jump, prefixies.visitor, prefixies.dm].includes(prefix) &&
                        this.resultActive
                    ) {
                        this.selectedSearch =
                            prefix === prefixies.jump
                                ? "jump"
                                : prefix === prefixies.new
                                ? "new"
                                : prefix === prefixies.visitor
                                ? "visitor"
                                : prefix === prefixies.dm
                                ? "dm"
                                : "";
                        this.searchText = "";
                        this.isTherePrefix = true;
                    }
                } else if (event.key === "Backspace" && !this.searchText.length) {
                    this.selectedSearch = null;
                    this.isTherePrefix = false;
                }
            };
            const searchInput = document.getElementById("search-bar-input");
            document.addEventListener("keydown", documentKeyDownFn);
            searchInput?.addEventListener("keydown", inputKeydownFn);
            this.listeners.push(() => {
                document.removeEventListener("click", clickListener);
                document.removeEventListener("keydown", documentKeyDownFn);
                searchInput?.removeEventListener("keydown", inputKeydownFn);
            });
        },
        beforeDestroy() {
            this.listeners.map((cancels) => cancels());
            this.notification.unsubscribe();
        },
        createTask() {
            this.addNewWindow({
                type: "create-task",
            });
        },
        searchItems() {
            const staticItems = staticSearchElements(this);
            const channelItems = this.channels.reduce((acc, channel) => {
                if (["whatsapp-qr", "whatsapp-v2", "whatsapp-business-api"].includes(channel.type)) {
                    acc.push({
                        prefix: "jump",
                        type: "navigator",
                        action: () =>
                            this.$router.push(
                                channel.type === "whatsapp-qr"
                                    ? `/integrations/whatsapp-v1/${channel._id}/detail`
                                    : channel.type === "whatsapp-v2"
                                    ? `/integrations/whatsapp-v2/${channel._id}/detail`
                                    : channel.type === "whatsapp-business-api"
                                    ? `/integrations/whatsapp-business-api/${channel._id}/detail`
                                    : "/"
                            ),
                        name:
                            channel.type === "whatsapp-qr"
                                ? `Integration Whatsapp QR - Channel Detail (${channel.name})`
                                : channel.type === "whatsapp-v2"
                                ? `Integration Whatsapp V2 - Channel Detail (${channel.name})`
                                : channel.type === "whatsapp-business-api"
                                ? `Integration Business API - Channel Detail (${channel.name})`
                                : "/",
                    });
                }
                return acc;
            }, []);
            const usersList = this.users.reduce((acc, user) => {
                acc.push({
                    prefix: "dm",
                    type: "navigator",
                    action: () => this.startDM(user._id),
                    name: `${user.name}`,
                });

                return acc;
            }, []);

            return [
                {
                    prefix: "new",
                    type: "navigator",
                    action: () => this.createTask(),
                    name: this.$t("message.createTask"),
                },
                ...channelItems,
                ...staticItems,
                ...usersList,
            ];
        },
    },
    async mounted() {
        this.onKeyboardShortcut();
        this.notification = systemMessageStream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.NOTIFICATION_SETTINGS))
            .subscribe(() => {
                this.openRespresentativeOptions = true;
                console.log("Pubsub is working");
            });
    },
    watch: {
        searchText(val) {
            if (val !== "" && !this.resultActive) this.toggleSearch();
            if (this.debounce) clearTimeout(this.debounce);
            let results = [];
            if (this.selectedSearch !== "visitor") {
                let items = this.searchItems();
                if (this.selectedSearch) items = items.filter((item) => item.prefix === this.selectedSearch);
                items = new Fuse(items, { includeScore: true, keys: ["name", ["prefix", "name"], "keywords"] });
                items = items.search(val).map((x) => x.item);
                results = items.map((item) => {
                    if (item.keywords) {
                        const fuse = new Fuse(item.keywords, { includeScore: true });
                        item.keywords = fuse.search(val).map((x) => x.item);
                    }
                    return item;
                });
            }
            this.debounce = setTimeout(() => {
                this.navigationIndex = 0;
                if (!["new", "jump", "dm"].includes(this.selectedSearch) && val.trim().length >= 3) {
                    searchVisitor(val).then((res) => {
                        this.results = [
                            ...results,
                            ...res.map((v) => ({
                                prefix: "visitor",
                                type: "navigator",
                                action: () => this.setVisitorDetailPopup(v._id),
                                name: `${v.fullname || "N/A"} - ${v.email || "N/A"}`,
                            })),
                        ];
                    });
                }
            }, 750);
            this.results = results;

            if (
                this.searchText === this.$t("search.prefix.new").toLowerCase() ||
                this.searchText === this.$t("search.prefix.new") ||
                this.searchText === this.$t("search.prefix.jump").toLowerCase() ||
                this.searchText === this.$t("search.prefix.jump") ||
                this.searchText === "dm" ||
                this.searchText === "DM"
            ) {
                this.isAnyPrefixTyped = true;
            } else {
                this.isAnyPrefixTyped = false;
            }
        },
        resultActive(newVal, oldVal) {
            const searchInput = document.getElementById("search-bar-input");
            if (newVal === false) {
                this.searchText = "";
                searchInput?.blur();
            } else {
                searchInput?.focus();
            }
        },
    },
};
</script>
<style lang="scss">
.representativeSettings {
    .ant-modal-body {
        padding: 24px 5px 24px 0px;
    }
}
.centered-name {
    align-items: center !important;
}

#search-bar {
    position: relative;
    z-index: 999;

    .typer-wrapper {
        position: absolute;
        /* left: 20px; */
        width: calc(100% - 49px);
        height: 100%;
        display: flex;
        align-items: center;
        padding-left: 37px;

        .vue-typer .custom.char.typed {
            color: var(--color-text-low-default);
        }
    }

    .input {
        position: relative;
        display: flex;
        align-items: center;
        border: 1px solid var(--color-border-default);
        width: 300px;
        border-radius: 5px;
        z-index: 1;
        background: var(--color-bg-neutral-default) !important;
        transition: all 0.5s;

        input {
            background: var(--color-bg-neutral-default) !important;
            color: var(--default-text-color) !important;
        }
        .material-icons {
            color: var(--default-text-color) !important;
        }

        &.active {
            width: 500px;
        }

        & input {
            border: none;
            outline: none;
            border-radius: 50px;
            padding: 10px;
            width: 100%;
        }

        .search-icon {
            margin-left: 10px;
        }

        .keyboard-shortcut {
            min-width: max-content;
            margin-right: 10px;
        }
    }

    .result-search {
        display: none;

        .items {
            display: flex;
            flex-direction: column;
            align-items: start;
            justify-content: flex-start;
            width: 100%;
            max-height: 40vh;
            overflow: auto;

            .item {
                width: 100%;
                display: flex;
                padding-left: 10px;
                padding: 10px;
                font-family: Roboto, "Noto Sans", -apple-system, BlinkMacSystemFont, sans-serif !important;
                color: var(--default-text-color) !important;

                &.active {
                    background: var(--color-bg-neutral-hover);
                }

                &:hover {
                    background: var(--color-bg-neutral-hover);
                }

                .badget {
                    border-radius: 100%;
                    width: 35px;
                }

                .content {
                    background: transparent !important;
                    margin-left: 5px;

                    .name {
                        font-weight: bold;
                        font-size: 14px;
                        line-height: 12px;
                    }

                    .keywords {
                        display: flex;
                        flex-direction: column;
                        flex-wrap: nowrap;
                        line-height: 10px;
                        font-size: 10px;

                        .matched {
                            font-weight: bold;
                        }
                    }
                }
            }
        }

        &.active {
            border-radius: 5px;
            position: absolute;
            background: var(--color-bg-neutral-default) !important;
            color: var(--default-text-color) !important;
            width: 100%;
            border: 1px solid var(--color-border-default);
            z-index: 0;
            display: flex !important;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            padding-top: 7px;
            margin-top: -8px;
            height: max-content;
        }

        .empty {
            padding-top: 50px;
            padding-bottom: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            text-align: center;
            font-family: Roboto, "Noto Sans", -apple-system, BlinkMacSystemFont, sans-serif !important;
            color: var(--default-text-color) !important;

            .search-icon {
                font-size: 50px;
            }

            .info-title {
                font-size: 20px;
                font-weight: bold;
            }

            .info-description {
                font-size: 14px;
                margin-top: 10px;
                display: flex;
                justify-content: center;
                align-items: center;
                flex-wrap: wrap;

                #keyboard-shortcut {
                    margin: 0 3px;
                }
            }
        }

        .result-bottom {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            border-top: 1px solid var(--color-border-default);
            padding: 3px;
            width: 100%;

            .text {
                margin: 0 3px;
            }
        }
    }
}
</style>
